/**
 * THE SCRIPT VIEW — the flow read top to bottom, and edited in place.
 *
 * This is the primary way to look at a flow: a numbered list of what the phone says, in the
 * order a caller hears it. It is not a picture of a state machine — it is the call, in
 * sentences, and every row is live. Click one to edit it in the inspector; press the + under
 * any step to add another after it; press the − on a step to remove it and heal the wire
 * through it. The diagram is one toggle away for anyone who wants the shape, but nobody has to
 * learn drag-and-drop to change what their line says at 2 AM.
 *
 * IT ALWAYS SHOWS THE FLOW — even one built from a template. A flow whose steps were not drawn
 * on the canvas has no graph to walk, so instead of a locked veil saying "nothing to draw
 * yet", this renders the compiled steps read-only, with one honest sentence about why it is
 * read-only and the single control that unlocks editing. Seeing the call you are about to
 * change is the whole point.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Badge, Callout, HelpTip, Icon, LinkAction } from '../index.ts';
import { NODE_SPECS, PALETTE_ORDER } from './model.ts';
import type { NdKind } from './model.ts';
import type { NdGraph } from './graph.ts';
import { describeStep, orderRows } from './scriptOrder.ts';
import type { ScriptRow, StepFace } from './scriptOrder.ts';
import { readThrough, summarise } from './readthrough.ts';
import type { Line } from './readthrough.ts';
import type { PlaybookStep } from '../../../../src/api/contract.ts';

export type ScriptViewProps = {
  graph: NdGraph;
  /** Compiled steps — the summary line, and the whole render when `stale`. */
  steps: PlaybookStep[];
  selectedId: string | null;
  readOnly: boolean;
  /** The flow's steps were not drawn here: render read-only from `steps`. */
  stale: boolean;
  /** True when the role could write if the flow were not stale — gates the unlock link. */
  canWrite: boolean;
  situationLabel: (key: string) => string;
  trigger: string;
  issues: Map<string, string[]>;
  onSelect: (id: string) => void;
  onAddAfter: (afterId: string, kind: NdKind) => void;
  onDelete: (id: string) => void;
  /** Acknowledge that drawing over a template flow replaces it. */
  onDrawOver: () => void;
};

export function ScriptView(props: ScriptViewProps): ReactNode {
  const { graph, steps, stale } = props;

  if (stale) {
    return <StaleScript steps={steps} canWrite={props.canWrite} onDrawOver={props.onDrawOver} />;
  }

  const { rows, orphans } = orderRows(graph);

  // Steps are numbered; the entry ("a call comes in") and the branch-case headers are not —
  // numbering them would lie about how many things the call does.
  let counter = 0;
  const numberFor = (row: ScriptRow): number | null => {
    if (row.type !== 'step' || row.node.type === 'entry') return null;
    counter += 1;
    return counter;
  };

  return (
    <section className="ndf-script" aria-label="The call, step by step">
      <header className="ndf-script-head">
        <div>
          <span className="nd-eyebrow">
            The call, step by step <HelpTip helpKey="flows.readthrough" />
          </span>
          <p className="ndf-script-summary">{summarise(steps)}</p>
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="ndf-script-empty">This flow has no steps yet. Add the first one below.</p>
      ) : (
        <ol className="ndf-script-list">
          {rows.map((row) => {
            if (row.type === 'case') {
              return (
                <li key={row.key} className="ndf-script-caseline" data-depth={row.depth}>
                  <span className="ndf-script-casechip">
                    if <b className="nd-mono">{row.value}</b>
                    <Icon name="arrow-right" size={11} />
                  </span>
                  {row.wired ? null : (
                    <span className="ndf-script-casenote">nothing here yet</span>
                  )}
                </li>
              );
            }
            const face = describeStep(row.node, props.situationLabel);
            return (
              <li key={row.key} className="ndf-script-item" data-depth={row.depth}>
                <StepRow
                  id={row.node.id}
                  kind={row.node.type as NdKind}
                  face={face}
                  number={numberFor(row)}
                  selected={props.selectedId === row.node.id}
                  readOnly={props.readOnly}
                  issue={props.issues.get(row.node.id) ?? []}
                  onSelect={() => props.onSelect(row.node.id)}
                  onAdd={(kind) => props.onAddAfter(row.node.id, kind)}
                  onDelete={() => props.onDelete(row.node.id)}
                />
              </li>
            );
          })}
        </ol>
      )}

      {orphans.length > 0 ? (
        <div className="ndf-script-orphans">
          <p className="nd-eyebrow">Not wired in yet</p>
          <p className="ndf-script-orphans-note">
            These steps exist but nothing leads to them. Open the diagram to wire them in, or
            remove them.
          </p>
          <ul>
            {orphans.map((node) => {
              const face = describeStep(node, props.situationLabel);
              return (
                <li key={node.id}>
                  <StepRow
                    id={node.id}
                    kind={node.type as NdKind}
                    face={face}
                    number={null}
                    selected={props.selectedId === node.id}
                    readOnly={props.readOnly}
                    issue={props.issues.get(node.id) ?? []}
                    onSelect={() => props.onSelect(node.id)}
                    onAdd={(kind) => props.onAddAfter(node.id, kind)}
                    onDelete={() => props.onDelete(node.id)}
                    loose
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// -------------------------------------------------------------------------------- a step row

type StepRowProps = {
  id: string;
  kind: NdKind;
  face: StepFace;
  number: number | null;
  selected: boolean;
  readOnly: boolean;
  issue: string[];
  onSelect: () => void;
  onAdd: (kind: NdKind) => void;
  onDelete: () => void;
  /** An orphan — no add tray, it is not part of the run. */
  loose?: boolean;
};

function StepRow(props: StepRowProps): ReactNode {
  const { face, kind } = props;
  const [addOpen, setAddOpen] = useState(false);
  const isEntry = kind === 'entry';
  const canDelete = !props.readOnly && !isEntry;

  return (
    <div className={`ndf-srow-wrap${props.loose ? ' is-loose' : ''}`}>
      <div
        className={[
          'ndf-srow',
          `ndf-sface--${face.tone}`,
          props.selected ? 'is-selected' : '',
          props.issue.length ? 'is-error' : '',
          isEntry ? 'is-entry' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="button"
        tabIndex={0}
        aria-pressed={props.selected}
        onClick={props.onSelect}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            props.onSelect();
          }
        }}
      >
        <span className="ndf-srow-num" aria-hidden="true">
          {props.number ?? <Icon name={isEntry ? 'phone-live' : 'dots'} size={13} />}
        </span>

        <span className="ndf-srow-chip" aria-hidden="true">
          <Icon name={face.icon} size={14} />
        </span>

        <div className="ndf-srow-main">
          <div className="ndf-srow-eyebrow">
            <span>{face.eyebrow}</span>
            {face.tag ? <span className="ndf-srow-tag nd-mono">{face.tag}</span> : null}
          </div>
          {face.quote ? (
            <p className="ndf-srow-quote">“{face.quote}”</p>
          ) : (
            <p className="ndf-srow-body">{face.body}</p>
          )}
          {face.sub ? <p className="ndf-srow-sub">{face.sub}</p> : null}
          {face.rule ? (
            <p className="ndf-srow-rule">
              <Icon name="lock" size={11} /> {face.rule}
            </p>
          ) : null}
          {props.issue.map((message) => (
            <p key={message} className="ndf-srow-issue">
              <Icon name="alert-triangle" size={11} /> {message}
            </p>
          ))}
        </div>

        {canDelete ? (
          <button
            type="button"
            className="ndf-srow-del"
            title="Remove this step"
            onClick={(event) => {
              event.stopPropagation();
              props.onDelete();
            }}
          >
            <Icon name="minus" size={13} />
            <span className="nd-sr">Remove this step</span>
          </button>
        ) : null}
      </div>

      {/* Add-after affordance. On a step in the middle of a finished flow this slots the new
          step into the wire; on a step with a free outlet it wires straight on. Either way it
          always lands IN the flow — see addKindAt in FlowEditor. */}
      {props.readOnly || props.loose ? null : (
        <div className="ndf-srow-addzone">
          {addOpen ? (
            <div className="ndf-srow-tray" role="group" aria-label="Add a step here">
              {PALETTE_ORDER.map((k) => {
                const spec = NODE_SPECS[k];
                return (
                  <button
                    key={k}
                    type="button"
                    className={`ndf-srow-traybtn ndf-sface--${spec.tone}`}
                    onClick={() => {
                      props.onAdd(k);
                      setAddOpen(false);
                    }}
                    title={spec.blurb}
                  >
                    <Icon name={spec.icon} size={13} />
                    {spec.label}
                  </button>
                );
              })}
              <button type="button" className="ndf-srow-traycancel" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="ndf-srow-addbtn" onClick={() => setAddOpen(true)}>
              <Icon name="plus" size={12} /> Add a step {isEntry ? 'first' : 'here'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the template (stale) render

/**
 * A flow built from a template or hand-edited has no drawing to walk, so it is rendered from
 * the compiled steps and is read-only. It STILL SHOWS — the whole complaint this view fixes is
 * that a template flow used to open as an empty canvas under a veil.
 */
function StaleScript({
  steps,
  canWrite,
  onDrawOver,
}: {
  steps: PlaybookStep[];
  canWrite: boolean;
  onDrawOver: () => void;
}): ReactNode {
  const lines = readThrough(steps);
  let counter = 0;

  return (
    <section className="ndf-script" aria-label="The call, step by step">
      <header className="ndf-script-head">
        <div>
          <span className="nd-eyebrow">
            The call, step by step <HelpTip helpKey="flows.readthrough" />
          </span>
          <p className="ndf-script-summary">{summarise(steps)}</p>
        </div>
        <Badge tone="ok" size="sm" dot>
          Answering now
        </Badge>
      </header>

      <Callout tone="warn" icon="alert-triangle">
        <b>This flow came ready-made.</b> Its steps were built from a template rather than drawn
        here, so they are shown as-is and cannot be edited step by step yet.{' '}
        {canWrite ? (
          <LinkAction onClick={onDrawOver} accent>
            Edit it as a flow
          </LinkAction>
        ) : null}{' '}
        {canWrite ? '— this opens it for editing and replaces the template.' : null}
      </Callout>

      <ol className="ndf-script-list is-readonly">
        {lines.map((line, index) => (
          <StaleLine key={`${index}-${line.text}`} line={line} number={line.kind === 'rule' ? null : (counter += 1)} />
        ))}
      </ol>
    </section>
  );
}

function StaleLine({ line, number }: { line: Line; number: number | null }): ReactNode {
  if (line.kind === 'case') {
    return (
      <li className="ndf-script-caseline" data-depth={0}>
        <span className="ndf-script-casechip">{line.text}</span>
      </li>
    );
  }
  return (
    <li className={`ndf-script-item ndf-script-item--${line.kind}`} data-depth={line.depth}>
      <div className="ndf-srow ndf-srow--readonly">
        <span className="ndf-srow-num" aria-hidden="true">
          {number ?? ''}
        </span>
        <div className="ndf-srow-main">
          {line.quote !== undefined ? (
            <>
              <p className="ndf-srow-body">{line.text}</p>
              <p className="ndf-srow-quote">“{line.quote}”</p>
            </>
          ) : (
            <p className={line.kind === 'rule' ? 'ndf-srow-rule' : 'ndf-srow-body'}>
              {line.kind === 'rule' ? <Icon name="lock" size={11} /> : null} {line.text}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

export default ScriptView;
